import {
    ErrorPage,
    type KeycloakContext,
    useAlerts,
} from "@keycloak/keycloak-ui-shared";
import {
    useEnvironment,
    type AccountEnvironment,
} from "@keycloak/keycloak-account-ui";
import { Page, Spinner, AlertVariant } from "@patternfly/react-core";
import { Suspense, useEffect } from "react";
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    type RouteObject,
    RouterProvider,
} from "react-router-dom";
import { type MenuItem, routes } from "../routes";
import { PageNav } from "./PageNav.tsx";
import { Header } from "./Header.tsx";
import { useTranslation } from "react-i18next";

function mapRoutes(
    context: KeycloakContext<AccountEnvironment>,
    content: MenuItem[],
): RouteObject[] {
    return content
        .map((item) => {
            if ("children" in item) {
                return mapRoutes(context, item.children);
            }

            // Do not add route disabled via feature flags
            if (item.isVisible && !context.environment?.features[item.isVisible]) {
                return null;
            }

            return item;
        })
        .filter((item) => !!item)
        .flat();
}

function CatchAllRedirect() {
    const { t } = useTranslation();
    const { addAlert } = useAlerts();

    useEffect(() => {
        addAlert(t("pageNotFound"), AlertVariant.warning);
    }, [addAlert, t]);

    return <Navigate to="." replace />;
}

export const Root = () => {
    const context = useEnvironment<AccountEnvironment>();
    const router = createBrowserRouter([
        {
            path: decodeURIComponent(new URL(context.environment.baseUrl).pathname),
            element: (
                <Page header={<Header />} sidebar={<PageNav />} isManagedSidebar>
                    <Suspense fallback={<Spinner />}>
                        <Outlet />
                    </Suspense>
                </Page>
            ),
            errorElement: <ErrorPage />,
            children: [
                ...mapRoutes(context, routes),
                { path: "*", element: <CatchAllRedirect /> },
          ],
        },
    ])
    return <RouterProvider router={router} />;
};